import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faExclamationTriangle,
  faFileCsv,
  faFileExcel,
  faSpinner,
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { BASE_URL } from "../../config";

const RESULT_COLUMNS = [
  { key: "trans_no", label: "Transaction No." },
  { key: "loa_no", label: "LOA No." },
  { key: "issued_by", label: "Issued By" },
  { key: "trans_date", label: "Transaction Date" },
  { key: "member_id", label: "Member ID" },
  { key: "member_name", label: "Member Name" },
  { key: "corp_code", label: "Corp Code" },
  { key: "provider_code", label: "Provider Code" },
  { key: "plan_code", label: "Plan Code" },
  { key: "coverage_code", label: "Coverage Code" },
  { key: "cancel_reason", label: "Cancel Reason" },
  { key: "cancelled_by", label: "Cancelled By" },
  { key: "cancelled_datetime", label: "Cancelled Date/Time" },
];

const LoaIssuerLookupPage = () => {
  const [lookupInput, setLookupInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [resultsSearchTerm, setResultsSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "trans_no", direction: "asc" });
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [error, setError] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState(null);

  const normalizeLookupInputToCsv = (rawInput) => {
    return rawInput
      .split(/[\r\n,]+/)
      .map((item) => item.trim())
      .filter(Boolean)
      .join(",");
  };

  const normalizeErrorMessage = (message) => {
    if (!message) return "An error occurred while searching for records.";
    if (typeof message === "string") return message;
    if (typeof message === "object") {
      return Object.values(message)
        .flat()
        .filter(Boolean)
        .join(" ");
    }
    return String(message);
  };

  const getSortableValue = (record, key) => {
    const value = record?.[key];
    if (value == null) return "";
    return String(value).toLowerCase();
  };

  const filteredAndSortedResults = useMemo(() => {
    const normalizedSearch = resultsSearchTerm.trim().toLowerCase();

    const filtered = !normalizedSearch
      ? [...results]
      : results.filter((record) =>
          RESULT_COLUMNS.some(({ key }) =>
            String(record?.[key] ?? "").toLowerCase().includes(normalizedSearch)
          )
        );

    return [...filtered].sort((a, b) => {
      const aVal = getSortableValue(a, sortConfig.key);
      const bVal = getSortableValue(b, sortConfig.key);
      const comparison = aVal.localeCompare(bVal, undefined, {
        numeric: true,
        sensitivity: "base",
      });

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [results, resultsSearchTerm, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }

      return { key, direction: "asc" };
    });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return "-";
    return sortConfig.direction === "asc" ? "^" : "v";
  };

  const handleSearch = async (e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }

    const normalizedLookupList = normalizeLookupInputToCsv(lookupInput);

    if (!normalizedLookupList) {
      Swal.fire({
        title: "Missing Input",
        text: "Please enter at least one LOA number or transaction number.",
        icon: "warning",
        confirmButtonColor: "#0ea5e9",
      });
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]);

    try {
      const token = localStorage.getItem("authentication/token");
      const tokenType = localStorage.getItem("authentication/token_type") || "Bearer";

      if (!token) {
        throw new Error("Authentication token not found. Please log in again.");
      }

      const response = await fetch(`${BASE_URL}/transactions/loa-issuer-lookup/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${tokenType} ${token}`,
        },
        body: JSON.stringify({ lookup_list: normalizedLookupList }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(normalizeErrorMessage(data.message));
      }

      if (data.status !== "success") {
        throw new Error(normalizeErrorMessage(data.message) || "Unknown error occurred.");
      }

      const foundRecords = data.data || [];
      setResults(foundRecords);
      setResultsSearchTerm("");
      setSortConfig({ key: "trans_no", direction: "asc" });
      setSearchCriteria(data.search_criteria);
      setSearchPerformed(true);

      if (foundRecords.length > 0) {
        Swal.fire({
          title: "Success",
          text: `Found ${foundRecords.length} record(s) matching your search criteria.`,
          icon: "success",
          confirmButtonColor: "#0ea5e9",
        });
      } else {
        Swal.fire({
          title: "No Results",
          text: "No records found matching the provided LOA or transaction numbers.",
          icon: "info",
          confirmButtonColor: "#0ea5e9",
        });
      }
    } catch (err) {
      const errorMsg = err.message || "An error occurred while searching for records.";
      setError(errorMsg);
      setSearchPerformed(true);

      Swal.fire({
        title: "Search Error",
        text: errorMsg,
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setLookupInput("");
    setResults([]);
    setResultsSearchTerm("");
    setSortConfig({ key: "trans_no", direction: "asc" });
    setSearchPerformed(false);
    setError(null);
    setSearchCriteria(null);
  };

  const buildExportRows = () => {
    return filteredAndSortedResults.map((record) => {
      const row = {};
      RESULT_COLUMNS.forEach(({ key, label }) => {
        row[label] = record?.[key] || "";
      });
      return row;
    });
  };

  const buildExportFileName = (ext) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    return `loa_issuer_lookup_results_${stamp}.${ext}`;
  };

  const escapeCsvValue = (value) => {
    const raw = value == null ? "" : String(value);
    return `"${raw.replace(/"/g, '""')}"`;
  };

  const handleExportCsv = () => {
    if (!filteredAndSortedResults.length) {
      Swal.fire({
        title: "No Data",
        text: "No results available to export.",
        icon: "info",
        confirmButtonColor: "#0ea5e9",
      });
      return;
    }

    const rows = buildExportRows();
    const headers = Object.keys(rows[0]);
    const csvLines = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => headers.map((header) => escapeCsvValue(row[header])).join(",")),
    ];

    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = buildExportFileName("csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  };

  const handleExportXlsx = async () => {
    if (!filteredAndSortedResults.length) {
      Swal.fire({
        title: "No Data",
        text: "No results available to export.",
        icon: "info",
        confirmButtonColor: "#0ea5e9",
      });
      return;
    }

    const rows = buildExportRows();
    const XLSX = await import("xlsx");
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "LOA Issuer Lookup");
    XLSX.writeFile(workbook, buildExportFileName("xlsx"));
  };

  const renderCell = (record, key) => {
    const value = record?.[key];
    if (value == null || value === "") {
      return <span className="text-slate-400">-</span>;
    }

    return String(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="mx-auto max-w-full">
        <div className="mb-6">
          <h1 className="flex items-center gap-3 text-3xl font-bold text-gray-900">
            <FontAwesomeIcon icon={faSearch} className="text-sky-600" />
            LOA Issuer Lookup
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Search LOA issuer records by LOA number, transaction number, or a mixed list of both.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
          <div className="space-y-6">
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-md">
              <form onSubmit={handleSearch}>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  LOA No. / Transaction No.(s) <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={lookupInput}
                  onChange={(e) => setLookupInput(e.target.value)}
                  placeholder={"Enter LOA no. or transaction no. separated by commas or one per line\nExample:\nLOA123\n250000001\nLOA456"}
                  className="min-h-[180px] w-full resize-y rounded-lg border-2 border-slate-300 px-4 py-3 font-mono text-sm focus:border-blue-500 focus:outline-none"
                  rows="7"
                  disabled={loading}
                />
                <p className="mt-2 text-xs text-gray-500">
                  Maximum 100 values per search.
                </p>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold transition-all ${
                      loading
                        ? "cursor-not-allowed bg-slate-300 text-gray-500"
                        : "bg-blue-600 text-white hover:bg-blue-700 active:scale-95"
                    }`}
                  >
                    {loading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSearch} />
                        Search
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={loading}
                    className="rounded-lg border-2 border-slate-300 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-slate-50 disabled:opacity-50"
                  >
                    Clear
                  </button>
                </div>
              </form>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow">
              <h3 className="mb-3 font-semibold text-gray-900">Accepted Input</h3>
              <div className="space-y-3 text-sm text-gray-700">
                <p>Paste LOA numbers, transaction numbers, or both in the same field.</p>
                <code className="block overflow-x-auto rounded bg-slate-100 p-3 font-mono text-xs text-gray-800">
                  LOA123,250000001
                </code>
                <code className="block overflow-x-auto rounded bg-slate-100 p-3 font-mono text-xs text-gray-800">
                  LOA123{"\n"}250000001{"\n"}LOA456
                </code>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {error && (
              <div className="rounded border-l-4 border-red-500 bg-red-50 p-4">
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faTimesCircle} className="mt-1 text-red-500" />
                  <div>
                    <h3 className="font-semibold text-red-800">Search Error</h3>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {searchPerformed && searchCriteria && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="mt-1 text-blue-600" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-blue-900">Search Summary</h3>
                    <p className="mt-1 text-sm text-blue-800">
                      {results.length > 0 ? `Found ${results.length} record(s)` : "No records found"} using{" "}
                      <strong>{searchCriteria.lookup_count} lookup value(s)</strong>.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {searchPerformed && results.length > 0 && (
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-md">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="text-sm text-slate-700">Export current search results</p>
                    <input
                      type="text"
                      value={resultsSearchTerm}
                      onChange={(e) => setResultsSearchTerm(e.target.value)}
                      placeholder="Search all columns"
                      className="w-72 max-w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={handleExportCsv}
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-blue-700"
                    >
                      <FontAwesomeIcon icon={faFileCsv} />
                      Export CSV
                    </button>
                    <button
                      type="button"
                      onClick={handleExportXlsx}
                      className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700"
                    >
                      <FontAwesomeIcon icon={faFileExcel} />
                      Export XLSX
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-300 bg-slate-100">
                        {RESULT_COLUMNS.map(({ key, label }) => (
                          <th key={key} className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-700">
                            <button
                              type="button"
                              onClick={() => handleSort(key)}
                              className="inline-flex items-center gap-1 hover:text-blue-600"
                            >
                              {label} <span className="text-xs">{getSortIndicator(key)}</span>
                            </button>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAndSortedResults.map((record, index) => (
                        <tr
                          key={`${record.trans_no || "trans"}-${record.loa_no || "loa"}-${index}`}
                          className={`border-b border-slate-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-slate-50"
                          } transition-colors hover:bg-slate-100`}
                        >
                          {RESULT_COLUMNS.map(({ key }) => (
                            <td
                              key={key}
                              className={`px-4 py-3 text-gray-800 ${
                                ["trans_no", "loa_no", "member_id", "corp_code", "provider_code", "plan_code", "coverage_code"].includes(key)
                                  ? "whitespace-nowrap font-mono"
                                  : "min-w-[150px]"
                              }`}
                            >
                              {renderCell(record, key)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm text-gray-600">
                  <span className="font-semibold">{filteredAndSortedResults.length}</span> of{" "}
                  <span className="font-semibold">{results.length}</span> record(s) displayed
                </div>
              </div>
            )}

            {searchPerformed && results.length === 0 && !error && (
              <div className="rounded border-l-4 border-amber-500 bg-amber-50 p-6 text-center">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mb-3 text-3xl text-amber-600" />
                <h3 className="mb-1 font-semibold text-amber-900">No Results Found</h3>
                <p className="text-sm text-amber-800">
                  No LOA issuer records match the provided LOA or transaction numbers.
                </p>
              </div>
            )}

            {!searchPerformed && (
              <div className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-12 text-center">
                <FontAwesomeIcon icon={faSearch} className="mb-4 text-4xl text-slate-400" />
                <h3 className="mb-2 font-semibold text-gray-700">Ready to Search</h3>
                <p className="mx-auto max-w-md text-sm text-gray-600">
                  Enter one or more LOA numbers or transaction numbers to find issuer details from loa_trans.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoaIssuerLookupPage;
